import { Directive, HostListener, inject, input, OnDestroy } from '@angular/core';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { EEventType, TEventFrontendTab } from '@enums/event-tracking.enum';

// Dwell: cursor must stay on the element this long before it counts as a real hover (filters out a
// cursor just passing through). Debounce: after firing, suppress re-fires for this long - collapses
// jitter at the element's edge (rapid enter/leave on a small icon) into a single event.
const HOVER_DWELL_MS = 150;
const HOVER_DEBOUNCE_MS = 1000;

/**
 * Attach to any info icon/tooltip trigger to fire campaign_analytics_info_button_hovered (#42).
 * campaignId auto-fills from EventTrackingService's campaign context - only currentTab and a
 * `source` identifying which info item this is need to be passed in.
 */
@Directive({
  selector: '[appTrackInfoHover]',
})
export class TrackInfoHoverDirective implements OnDestroy {
  public readonly appTrackInfoHover = input.required<string>();
  public readonly currentTab = input<TEventFrontendTab>();

  private readonly eventTrackingService = inject(EventTrackingService);

  private dwellTimeoutId: ReturnType<typeof setTimeout> | undefined;
  private lastFiredAt = 0;

  @HostListener('mouseenter')
  public onMouseEnter(): void {
    if (Date.now() - this.lastFiredAt < HOVER_DEBOUNCE_MS) {
      return;
    }

    this.dwellTimeoutId = setTimeout(() => {
      this.dwellTimeoutId = undefined;
      this.lastFiredAt = Date.now();

      this.eventTrackingService.track(EEventType.campaignAnalyticsInfoButtonHovered, {
        currentTab: this.currentTab(),
        source: this.appTrackInfoHover(),
      });
    }, HOVER_DWELL_MS);
  }

  @HostListener('mouseleave')
  public onMouseLeave(): void {
    this.clearDwellTimeout();
  }

  public ngOnDestroy(): void {
    this.clearDwellTimeout();
  }

  private clearDwellTimeout(): void {
    if (this.dwellTimeoutId !== undefined) {
      clearTimeout(this.dwellTimeoutId);
      this.dwellTimeoutId = undefined;
    }
  }
}
